import { cloneDeep } from 'lodash';

import { StringHelpers } from '../../service/StringHelpers/StringHelpers';

export interface TreeNode<T extends TreeNode<T>> {
    inner?: T[] | null;
    name: string;
    id: string;
}

export class TreeHelpers {
    /**
     * Метод ищет ноду для которой matcher вернет true
     * @param {T[]} nodes дерево в котором происходит поиск
     * @param {(node: T) => boolean} matcher проверка условия для текущей ноды
     * @returns {T | undefined} результат поиска
     */
    static find<T extends TreeNode<T>>(
        nodes: T[],
        matcher: (node: T) => boolean
    ): T | undefined {
        if (!nodes || nodes.length === 0) {
            return undefined;
        }

        let result: T | undefined;

        for (let index = 0; index < nodes.length; index++) {
            const node = nodes[index];

            if (matcher(node)) {
                result = node;
            } else if (node.inner) {
                result = TreeHelpers.find(node.inner, matcher);
            } else {
                result = undefined;
            }

            if (result) {
                break;
            }
        }

        return result;
    }

    /**
     * Рекурсивный обход дерева, вызывает callback для каждой вершины
     * @param {T[]} rootNodes дерево
     * @param {(node: T, parent: (T | undefined)) => void} cb callback для текущей вершины
     */
    static each<T extends TreeNode<T>>(
        rootNodes: T[],
        cb: (node: T, parent: T | undefined) => void
    ): void {
        const eachWithParent = (node: T, parent: T | undefined): void => {
            cb(node, parent);
            if (node.inner) {
                node.inner.forEach(child => eachWithParent(child, node));
            }
        };

        rootNodes.forEach(rootNode => {
            eachWithParent(rootNode, undefined);
        });
    }

    /**
     * Создает коллекцию с данными о родителе каждой ноды
     * @param {T[]} rootNodes дерево
     * @returns {Map}
     */
    static toRelationMap<T extends TreeNode<T>>(
        rootNodes: T[]
    ): Map<T, T | undefined> {
        const nodesMap = new Map<T, T | undefined>();
        TreeHelpers.each(rootNodes, (currentNode, parentNode) => {
            nodesMap.set(currentNode, parentNode);
        });
        return nodesMap;
    }

    /**
     * Метод возвращает путь (список нод) до заданной ноды,
     * путь представляет собой последовательность индексов от ноды до родителя (не включая индекс ноды)
     * @param {Map<T extends TreeNode<T>, T | undefined>} relationMap map, характеризующий родителя ноды
     * @param {T} node нода, для которой происходит поиск
     * @returns {T[]}
     */
    static findPath<T extends TreeNode<T>>(
        relationMap: Map<T, T | undefined>,
        node: T
    ): T[] {
        const path: T[] = [];
        let parent = relationMap.get(node);
        while (parent) {
            path.push(parent);
            parent = relationMap.get(parent);
        }

        return path;
    }

    /**
     * Метод возвращает список родителей для заданной ноды
     * @param {T[]} rootNodes дерево
     * @param {T} node нода, для которой происхожит поиск
     * @returns {TreeNode<T>[]}
     */
    static getParents<T extends TreeNode<T>>(rootNodes: T[], node: T): T[] {
        return TreeHelpers.findPath(TreeHelpers.toRelationMap(rootNodes), node);
    }

    /**
     * Метод возвращает пути (список id) до заданного списка id вершин
     * @param {T[]} treesList дерево
     * @param {string[]} listIds список id, для которых происходит поиск
     * @returns {string[]}
     */
    static getIdsPathToCurrentNodes<T extends TreeNode<T>>(
        treesList: T[],
        listIds: string[]
    ): string[] {
        let result: string[] = listIds;

        listIds.forEach(id => {
            const node = TreeHelpers.getNodeById(treesList, id);

            if (node) {
                result = result.concat(
                    TreeHelpers.getParents(treesList, node).map(
                        pathItem => pathItem.id
                    )
                );
            }
        });

        return result;
    }

    /**
     * Метод возвращает ноду по id
     * @param {T[]} treesList дерево
     * @param {string} id индекс искомой ноды
     * @returns {T | undefined}
     */
    static getNodeById<T extends TreeNode<T>>(
        treesList: T[],
        id: string
    ): T | undefined {
        return TreeHelpers.find(treesList, nodeItem => nodeItem.id === id);
    }

    /**
     * Метод получения списка идентификаторов детей списка нод
     * @param {T[]} nodes список нод для поиска
     * @returns {string[]}
     */
    static getChildNodesIds<T extends TreeNode<T>>(nodes: T[]): string[] {
        const listIds: string[] = [];

        TreeHelpers.each(nodes, currentNode => {
            listIds.push(currentNode.id);
        });

        return listIds;
    }

    /**
     * Метод получения отфильтрованного дерева, вершина присутсвует в отфильтрованном дереве если:
     * 1) находится в списке listNodeIds
     * 2) является предком, или потомком вершины из listNodeIds
     * @param {T[]} trees - дерево
     * @param {string[]} listNodeIds список id, по которым происходит фильтрация
     * @param {boolean} isParentInListNodeIds флаг характеризует, находился ли предок вершины в listNodeIds
     * @returns {T[]}
     */
    static getFilteredTreeByListNodeIds<T extends TreeNode<T>>(
        trees: T[],
        listNodeIds: string[],
        isParentInListNodeIds?: boolean
    ): T[] {
        if (trees.length === 0) {
            return [];
        }

        const listNodes: T[] = [];

        for (let i = 0; i < trees.length; i++) {
            const tree = cloneDeep(trees[i]);

            const filteredTree = tree.inner
                ? TreeHelpers.getFilteredTreeByListNodeIds(
                      tree.inner,
                      listNodeIds,
                      isParentInListNodeIds || listNodeIds.includes(tree.id)
                  )
                : [];

            if (filteredTree.length > 0) {
                tree.inner = filteredTree;
                listNodes.push(tree);
            } else if (isParentInListNodeIds || listNodeIds.includes(tree.id)) {
                listNodes.push(tree);
            }
        }

        return listNodes;
    }

    /**
     * Метод получения списка первых нод дерева, которые выбраны/не выбраны, в зависимости от findSelected
     * (если нода соответствует findSelected - поиск нод в детях производиться не будет)
     * @param {T} tree дерево
     * @param {Record<number, boolean>} selectedMap выбранные элементы дерева
     * @param {boolean} findSelected искомое значение для нод
     * @returns {Array<T>}
     */
    static getFirstEqualsTreeNodesList<T extends TreeNode<T>>(
        tree: T,
        selectedMap: Record<number, boolean>,
        findSelected: boolean
    ): Array<T> {
        if (!tree.inner) {
            return [];
        }

        let listNodes: Array<T> = [];

        tree.inner.forEach(item => {
            if (Boolean(selectedMap[item.id]) !== findSelected) {
                listNodes = listNodes.concat(
                    TreeHelpers.getFirstEqualsTreeNodesList(
                        item,
                        selectedMap,
                        findSelected
                    )
                );
            } else {
                listNodes.push(item);
            }
        });

        return listNodes;
    }

    /**
     * Метод обновления значений selectedMap или expandedMap
     * @param {string[]} listIds список изменяемых индексов
     * @param {Record<string, boolean>} currentMap текущий map
     * @param {boolean} newSelectedValue новое значение для элементов из списка listIds
     * @returns {Record<number, boolean>}
     */
    static updateMapForCurrentIds(
        listIds: string[],
        currentMap: Record<string, boolean>,
        newSelectedValue: boolean
    ): Record<number, boolean> {
        const updatedSelectedMap: Record<number, boolean> = Object.keys(
            currentMap
        ).reduce((acc, id) => {
            if (!listIds.includes(id)) {
                acc[id] = currentMap[id];
            }

            return acc;
        }, {});

        if (newSelectedValue) {
            listIds.forEach(id => {
                updatedSelectedMap[id] = newSelectedValue;
            });
        }

        return updatedSelectedMap;
    }

    /**
     * Метод получения списка идентификаторов, для тех вершин в которых searchText является префиксом поля name
     * @param {T[]} treesList дерево
     * @param {string} searchText искомый префикс
     * @returns {string[]}
     */
    static searchIdsNodesByName<T extends TreeNode<T>>(
        treesList: T[],
        searchText: string
    ): string[] {
        const foundedIdsNodes: string[] = [];

        TreeHelpers.each(treesList, tree => {
            if (StringHelpers.indexOfSubstring(tree.name, searchText) !== -1) {
                foundedIdsNodes.push(tree.id);
            }
        });

        return foundedIdsNodes;
    }

    /**
     * Метод получения параметров дерева в результате поиска
     * @param {T[]} treesList дерево
     * @param {string} searchText искомый префикс
     * @returns {string[]}
     */
    static getUpdatedBySearchTreeParams<T extends TreeNode<T>>(
        tree: T[],
        searchValue: string
    ): {
        filteredMap: Record<number, boolean>;
        expandedMap: Record<number, boolean>;
        listFoundedIds: string[];
    } {
        const listFoundedIds = TreeHelpers.searchIdsNodesByName(
            tree,
            searchValue
        );

        const listIdsPathsToFoundedIds = TreeHelpers.getIdsPathToCurrentNodes(
            tree,
            listFoundedIds
        );

        const searchTree = TreeHelpers.getFilteredTreeByListNodeIds<T>(
            tree,
            listFoundedIds
        );

        const filteredMap = TreeHelpers.updateMapForCurrentIds(
            TreeHelpers.getChildNodesIds(searchTree),
            {},
            true
        );

        const expandedMap = TreeHelpers.updateMapForCurrentIds(
            listIdsPathsToFoundedIds,
            {},
            true
        );

        return {
            filteredMap,
            expandedMap,
            listFoundedIds
        };
    }
}
